import { cva } from 'class-variance-authority';
import type { TFunction } from 'i18next';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/base';
import { UserRole } from '@/model/User.model';
import { toClassNames } from '@/utils';

interface Props {
  role: UserRole;
  className?: string;
}

const roleBadgeText: Record<UserRole, (t: TFunction<'schoolApp'>) => string> = {
  [UserRole.SchoolGroupAdmin]: (t) => t('schoolApp:role.school_group_admin'),
  [UserRole.SchoolAdmin]: (t) => t('schoolApp:role.school_admin'),
  [UserRole.Instructor]: (t) => t('schoolApp:role.instructor'),
  [UserRole.Student]: (t) => t('schoolApp:role.student'),
  [UserRole.Admin]: (t) => t('schoolApp:role.admin'), // Unused role in SchoolApp
};

const roleBadgeCva = cva('flex items-center justify-center rounded p-1.5 font-semibold', {
  variants: {
    variant: {
      [UserRole.SchoolGroupAdmin]: 'bg-gradient-to-r from-primary-600 to-primary-700 text-white',
      [UserRole.SchoolAdmin]: 'bg-gradient-to-r from-primary-400 to-primary-500 text-white',
      [UserRole.Instructor]: 'bg-gradient-to-r from-gray-500 to-gray-600 text-white',
      [UserRole.Student]: 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-500',
      [UserRole.Admin]: 'text-gray-500', // Unused role in SchoolApp
    } satisfies Record<UserRole, string>,
  },
});

const RoleBadge: FC<Props> = ({ role, className }) => {
  const { t } = useTranslation('schoolApp');

  return (
    <div className={toClassNames(roleBadgeCva({ variant: role }), className)}>
      <Text variant="xs">{roleBadgeText[role](t)}</Text>
    </div>
  );
};

export default RoleBadge;
